import { IContactDeprecatedService } from '@agent/core/contact/contact-deprecated.service';
import { HttpDeprecatedService } from '@commons/core/transport/http-deprecated.service';
import { IContact } from '@commons/models/contact';
import { ILogService, IPromise, IQService } from 'angular';
import { filter } from 'rxjs';

import { BookingStatus, Option } from '@contact/upgrade';

const MODULE_NAME = 'scriptArgument';
export const SOLSTICE_FACADE_PREFIX = 'protected/solstice';

export interface IScriptArgumentService {
  getFirstPage(scriptArgumentUuid: string): IPromise<string>;

  getScriptAgentWithContactSelected(): IPromise<string>;

  getScriptAgent(contact: IContact): IPromise<string>;
}

export class ScriptArgumentService implements IScriptArgumentService {
  static $inject: Array<string> = ['$log', '$q', 'httpDeprecated', 'contactDeprecatedService'];

  public name = MODULE_NAME;

  private PREFIX: string = '/scripting-agent-generated/';
  private scripts: { [responseId: string]: string } = {};

  constructor(
    private readonly $log: ILogService,
    private readonly $q: IQService,
    private readonly httpDeprecated: HttpDeprecatedService,
    private readonly contactDeprecatedService: IContactDeprecatedService
  ) {
    // disabling outgoing call tab when the agent has finished with a contact on this channel.
    this.contactDeprecatedService
      .contactState$()
      .pipe(filter(event => event.state === BookingStatus.CLOSED))
      .subscribe(event => this.removeScript(event.contact.responseId));
  }

  getFirstPage(scriptArgumentUuid: string): IPromise<string> {
    let deferred = this.$q.defer<string>();

    let _url = SOLSTICE_FACADE_PREFIX + '/getFirstPageScriptArgument';
    let args = [scriptArgumentUuid];
    this.httpDeprecated
      .asPromise()
      .withConfig({
        headers: {
          'Content-Type': 'text/plain; charset=UTF-8',
        },
      })
      .get(_url, args)
      .then(
        success => {
          this.$log.info(`[script argument] first page url = ${success.data}`);
          deferred.resolve(success.data);
        },
        error => {
          this.$log.error(
            `[script argument] get first page on script argument uuid [${scriptArgumentUuid}] failed :[${JSON.stringify(error)}]`
          );
          deferred.reject(`[script argument] get first page on script argument uuid [${scriptArgumentUuid}] failed`);
        }
      );
    return deferred.promise;
  }

  getScriptAgentWithContactSelected(): IPromise<string> {
    return this.contactDeprecatedService.getSelectedContact().then(contact => this.getScriptAgent(contact));
  }

  getScriptAgent(contact: IContact): IPromise<string> {
    let deferred = this.$q.defer<string>();

    let oldScript: Option<string> = this.getScript(contact.responseId);
    if (oldScript.isDefined()) {
      deferred.resolve(oldScript.get());
    } else if (contact.campaign && contact.campaign?.campaign.scriptArgumentUuid) {
      return this.getFirstPage(contact.campaign?.campaign.scriptArgumentUuid).then(_firstPage => {
        let url = this.PREFIX + _firstPage;
        this.addScript(contact.responseId, url);
        return url;
      });
    } else {
      deferred.reject(`[script argument] get script agent with responseId [${contact.responseId}] failed`);
    }
    return deferred.promise;
  }

  private getScript(responseId: string): Option<string> {
    return Option.of(this.scripts[responseId]);
  }

  private addScript(responseId: string, value: string) {
    this.scripts[responseId] = value;
  }

  private removeScript(responseId: string) {
    this.getScript(responseId).forEach(url => {
      delete this.scripts[responseId];
      this.$log.info(`[script argument] Removed script with url[${url}] corresponding to responseId[${responseId}]`);
    });
  }
}

export default MODULE_NAME;
